
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { CommunityMember } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { AutosuggestInput } from "../nurse/AutosuggestInput";

interface CommunityMemberDetailsProps {
  member: CommunityMember;
  onBack: () => void;
}

export function CommunityMemberDetails({ member: initialMember, onBack }: CommunityMemberDetailsProps) {
  const [complaint, setComplaint] = useState("");
  const [complaints, setComplaints] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState("");
  const [diagnoses, setDiagnoses] = useState<string[]>([]);
  const [treatment, setTreatment] = useState("");
  const [treatments, setTreatments] = useState<string[]>([]);
  const [temperature, setTemperature] = useState<string>("36.2");
  const [temperatureError, setTemperatureError] = useState<string>("");
  const [requiresFollowup, setRequiresFollowup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: nurseProfile } = useQuery({
    queryKey: ['nurseProfile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user?.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  const { data: activeTermData } = useQuery({
    queryKey: ['activeTermData'],
    queryFn: async () => {
      const { data: activeYear, error: yearError } = await supabase
        .from('academic_years')
        .select('id')
        .eq('is_active', true)
        .maybeSingle();

      if (yearError) throw yearError;
      if (!activeYear) throw new Error('No active academic year found');

      const { data: activeTerm, error: termError } = await supabase
        .from('terms')
        .select('id')
        .eq('academic_year_id', activeYear.id)
        .eq('is_active', true)
        .maybeSingle();

      if (termError) throw termError;
      if (!activeTerm) throw new Error('No active term found');

      return {
        academicYearId: activeYear.id,
        termId: activeTerm.id
      };
    }
  });

  const { data: visits, refetch: refetchVisits } = useQuery({
    queryKey: ['memberVisits', initialMember.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('visits')
        .select(`
          id,
          complaint,
          diagnosis,
          temperature,
          treatment,
          date,
          requires_followup,
          community_member_id
        `)
        .eq('community_member_id', initialMember.id)
        .order('date', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const validateTemperature = (value: string) => {
    const temp = parseFloat(value);
    if (isNaN(temp)) {
      setTemperatureError("Please enter a valid number");
      return false;
    }
    if (temp < 32.5 || temp > 42) {
      setTemperatureError("Temperature must be between 32.5°C and 42°C");
      return false;
    }
    setTemperatureError("");
    return true;
  };

  const handleTemperatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemperature(value);
    validateTemperature(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Basic validation
      if (!user?.schoolId) {
        toast({ title: "Error", description: "School ID not found", variant: "destructive" });
        return;
      }

      if (!nurseProfile?.id) {
        toast({ title: "Error", description: "Nurse profile not found", variant: "destructive" });
        return;
      }

      if (!activeTermData) {
        toast({ title: "Error", description: "No active term found", variant: "destructive" });
        return;
      }

      if (complaints.length === 0) {
        toast({ title: "Error", description: "Please add at least one complaint", variant: "destructive" });
        return;
      }

      if (!validateTemperature(temperature)) {
        toast({ title: "Error", description: "Please enter a valid temperature", variant: "destructive" });
        return;
      }

      if (diagnoses.length === 0) {
        toast({ title: "Error", description: "Please add at least one diagnosis", variant: "destructive" });
        return;
      }

      const visitData = {
        community_member_id: initialMember.id,
        nurse_id: nurseProfile.id,
        complaint: complaints.join(", "),
        diagnosis: diagnoses.join(", "),
        temperature: parseFloat(temperature),
        treatment: treatments.join(", "),
        academic_year_id: activeTermData.academicYearId,
        term_id: activeTermData.termId,
        requires_followup: requiresFollowup,
        school_id: user.schoolId,
        date: new Date().toISOString(),
      };

      const { error: visitError } = await supabase
        .from('visits')
        .insert([visitData]);

      if (visitError) {
        console.error('Visit creation error:', visitError);
        throw visitError;
      }

      toast({
        title: "Success",
        description: "Visit record saved successfully",
      });

      // Reset form
      setComplaints([]);
      setDiagnoses([]);
      setTreatments([]);
      setTemperature("36.2");
      setRequiresFollowup(false);
      
      // Refresh data
      refetchVisits();
      queryClient.invalidateQueries({ queryKey: ['todayVisits'] });
      queryClient.invalidateQueries({ queryKey: ['weekVisits'] });

    } catch (error) {
      console.error("Error saving visit:", error);
      toast({
        title: "Error",
        description: "Failed to save visit record",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVisit = async (visitId: string) => {
    if (!confirm("Are you sure you want to delete this visit?")) return;
    
    try {
      const { error } = await supabase
        .from("visits")
        .delete()
        .eq('id', visitId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Visit deleted successfully",
      });

      refetchVisits();
      queryClient.invalidateQueries({ queryKey: ['todayVisits'] });
      queryClient.invalidateQueries({ queryKey: ['weekVisits'] });
    } catch (error) {
      console.error("Error deleting visit:", error);
      toast({
        title: "Error",
        description: "Failed to delete visit",
        variant: "destructive",
      });
    }
  };

  // Helper functions for complaint/diagnosis/treatment management
  const addComplaint = (item: string) => {
    if (!complaints.includes(item)) {
      setComplaints([...complaints, item]);
    }
  };

  const removeComplaint = (item: string) => {
    setComplaints(complaints.filter((i) => i !== item));
  };

  const addDiagnosis = (item: string) => {
    if (!diagnoses.includes(item)) {
      setDiagnoses([...diagnoses, item]);
    }
  };

  const removeDiagnosis = (item: string) => {
    setDiagnoses(diagnoses.filter((i) => i !== item));
  };

  const addTreatment = (item: string) => {
    if (!treatments.includes(item)) {
      setTreatments([...treatments, item]);
    }
  };

  const removeTreatment = (item: string) => {
    setTreatments(treatments.filter((i) => i !== item));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <h2 className="text-2xl font-bold">{initialMember.name}</h2>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Treatment Form */}
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Complaints</Label>
              <AutosuggestInput
                type="COMPLAINT"
                value={complaint}
                onChange={setComplaint}
                placeholder="Enter complaints"
                onAddItem={addComplaint}
                selectedItems={complaints}
                onRemoveItem={removeComplaint}
              />
            </div>

            <div className="space-y-2">
              <Label>Temperature</Label>
              <Input
                type="number"
                step="0.1"
                value={temperature}
                onChange={handleTemperatureChange}
                placeholder="Enter temperature in °C"
                className={temperatureError ? "border-red-500" : ""}
              />
              {temperatureError && (
                <p className="text-sm text-red-500 mt-1">{temperatureError}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Diagnosis</Label>
              <AutosuggestInput
                type="DIAGNOSIS"
                value={diagnosis}
                onChange={setDiagnosis}
                placeholder="Enter diagnosis"
                onAddItem={addDiagnosis}
                selectedItems={diagnoses}
                onRemoveItem={removeDiagnosis}
              />
            </div>

            <div className="space-y-2">
              <Label>Treatment</Label>
              <AutosuggestInput
                type="TREATMENT"
                value={treatment}
                onChange={setTreatment}
                placeholder="Enter treatment"
                onAddItem={addTreatment}
                selectedItems={treatments}
                onRemoveItem={removeTreatment}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="followup"
                checked={requiresFollowup}
                onCheckedChange={(checked) => setRequiresFollowup(!!checked)}
              />
              <label
                htmlFor="followup"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Requires Follow-up
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Treatment'
              )}
            </Button>
          </form>
        </Card>

        {/* Visit History */}
        <Card className="p-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Visit History</h3>
            <div className="space-y-4">
              {visits && visits.length > 0 ? (
                visits.map((visit) => (
                  <div key={visit.id} className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">
                        {format(new Date(visit.date), 'PPP')}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteVisit(visit.id)}
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive flex items-center gap-1"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete</span>
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(visit.date), 'p')}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2 mt-2">
                      <div>
                        <div className="font-medium">Complaint</div>
                        <div className="text-sm text-muted-foreground">{visit.complaint}</div>
                      </div>
                      <div>
                        <div className="font-medium">Diagnosis</div>
                        <div className="text-sm text-muted-foreground">{visit.diagnosis}</div>
                      </div>
                      <div>
                        <div className="font-medium">Temperature</div>
                        <div className="text-sm text-muted-foreground">{visit.temperature}°C</div>
                      </div>
                      <div>
                        <div className="font-medium">Treatment</div>
                        <div className="text-sm text-muted-foreground">{visit.treatment}</div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No visit history available
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
